import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ShieldCheck, ArrowLeft, FileText, Scale } from 'lucide-react';

/**
 * LegalPage — public, no-auth Terms of Service + Privacy Policy.
 *
 * These are intentionally plain-English and short — reflecting the
 * architecture (no ads, no analytics, no data-mining) and the invite-only
 * posture. A real public launch would want a lawyer to review, but this
 * covers the honest shape of the service: we don't have data we could
 * monetize even if we wanted to.
 *
 * Route: /legal and /legal/privacy, /legal/terms (all render the same
 * page with anchor scrolling for sharing).
 */
export const LegalPage = () => {
  React.useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="legal-header-home"
          >
            <ShieldCheck className="w-6 h-6 text-primary" />
            <span className="font-semibold tracking-tight">Government Shield</span>
          </Link>
          <Button
            asChild
            variant="ghost"
            size="sm"
            data-testid="legal-back-to-app"
          >
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Back
            </Link>
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-16">
        {/* Intro */}
        <section className="space-y-4">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            Legal & Privacy
          </h1>
          <p className="text-slate-400 leading-relaxed">
            Government Shield is an independent, invite-only encrypted
            messenger. These pages spell out the contract between us (the
            operators of the server you connected to) and you (someone with
            a valid invite). They are intentionally short, because the
            architecture itself does most of the work.
          </p>
          <p className="text-xs text-slate-500">
            Last updated: 20 April 2026.
          </p>
        </section>

        {/* Terms */}
        <section id="terms" className="space-y-6 scroll-mt-24">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Scale className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Terms of Service</h2>
          </div>

          <TermsSection title="1. What this service is">
            Government Shield is an end-to-end encrypted messenger. Access
            is invite-only. The operator of the server you're connected to
            is responsible for their instance; the project itself is
            open-source software and is not affiliated with any government,
            intelligence agency, or law-enforcement body.
          </TermsSection>

          <TermsSection title="2. Your account">
            You are responsible for keeping your password and Recovery Key
            safe. Recovery Keys are shown once, at registration. If you
            lose both your password and your Recovery Key, your account
            cannot be recovered — the server cannot read your messages or
            reset your identity.
          </TermsSection>

          <TermsSection title="3. Acceptable use">
            Don't use Government Shield to harass, defraud, or harm other
            people. Don't distribute child sexual abuse material. Don't use
            it to plan violence. Server operators may revoke your account
            (and the cascade of accounts you invited) if you break these
            rules — your account disappears, but the forward-only referral
            chain is preserved so the audit trail remains intact.
          </TermsSection>

          <TermsSection title="4. No warranty">
            The service is provided as-is. We make our best effort at
            security, but no system is perfect. Use your own judgment
            about what to share through it. If you need a specific
            audit, the code is open source — read it, hire someone, or
            self-host.
          </TermsSection>

          <TermsSection title="5. Changes">
            If these terms change materially, we'll update the "last
            updated" date above. Your continued use after that date
            constitutes agreement.
          </TermsSection>
        </section>

        {/* Privacy */}
        <section id="privacy" className="space-y-6 scroll-mt-24">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Privacy Policy</h2>
          </div>

          <TermsSection title="What we store">
            <ul className="list-disc pl-5 space-y-2 text-slate-400">
              <li>
                <strong className="text-slate-200">Account metadata:</strong>{' '}
                username, email (used only for login + waitlist
                notifications), bcrypt hash of your password, bcrypt hash
                of your Recovery Key, a monotonic <code>token_version</code>{' '}
                counter for session revocation, your public key history,
                and — if you used an invite — the user ID of the person who
                invited you.
              </li>
              <li>
                <strong className="text-slate-200">Conversations:</strong>{' '}
                encrypted ciphertext + encrypted per-recipient keys. We
                cannot read these. They're auto-deleted 24 hours after
                they're sent.
              </li>
              <li>
                <strong className="text-slate-200">Files:</strong> encrypted
                blobs + SHA-256 per chunk (for integrity). We cannot read
                them. Also auto-deleted after 24 hours.
              </li>
              <li>
                <strong className="text-slate-200">Transient:</strong> IP
                address is used for rate-limiting only (keyed on a short
                window, never persisted). WebSocket connections track
                online/offline status.
              </li>
            </ul>
          </TermsSection>

          <TermsSection title="What we don't store">
            <ul className="list-disc pl-5 space-y-2 text-slate-400">
              <li>Message content in any readable form.</li>
              <li>Private keys — those never leave your device.</li>
              <li>Analytics, tracking pixels, third-party cookies.</li>
              <li>Advertising profiles (we don't run ads).</li>
              <li>Backups of expired messages.</li>
            </ul>
          </TermsSection>

          <TermsSection title="Third parties">
            The server you're connected to may use: a managed MongoDB
            provider (stores only encrypted data), an optional LiveKit
            server for voice channels (end-to-end encrypted when enabled),
            and a transactional email provider for waitlist approvals. No
            third party receives decrypted message content.
          </TermsSection>

          <TermsSection title="Your rights">
            You can delete your account at any time from Settings → Privacy
            → Delete Account. This permanently removes your account, your
            messages, your files, and the invites you issued. A PII-free
            tombstone remains in the referral chain so the forward-only
            audit trail isn't broken.
          </TermsSection>

          <TermsSection title="Law enforcement">
            We cannot disclose message content because we do not have
            access to it. We will respond to valid legal process requesting
            the metadata listed above. We publish a transparency report
            annually with aggregate figures.
          </TermsSection>
        </section>

        {/* Footer back */}
        <div className="pt-8 border-t border-border text-center">
          <Button asChild variant="outline" data-testid="legal-footer-back">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Government Shield
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
};

const TermsSection = ({ title, children }) => (
  <div className="p-5 rounded-xl bg-card/40 border border-border">
    <h3 className="text-lg font-semibold mb-3 text-slate-100">{title}</h3>
    <div className="text-sm text-slate-400 leading-relaxed">{children}</div>
  </div>
);

export default LegalPage;
